import { Link, useParams } from "react-router-dom";
import Skeleton from "react-loading-skeleton";
import { useMenuItem } from "../../hooks/useMenu";
import { formatCents } from "../../utils/money";
import NotFound404 from "../NotFound404";
import ComingSoonBadge from "../../components/ComingSoonBadge";

/** Full-width image placeholder for items without an imageUrl. */
function HeroPlaceholder({ name }) {
  return (
    <div
      className="w-full h-64 md:h-80 bg-charcoal flex items-center justify-center"
      aria-hidden="true"
    >
      <span className="font-display text-6xl text-aluminum/30">
        {name?.charAt(0) ?? "☕"}
      </span>
    </div>
  );
}

/** Category pill badge. */
function CategoryPill({ name }) {
  return (
    <span className="inline-block bg-mustard text-charcoal font-heading tracking-wider text-xs px-3 py-1 rounded-full border-2 border-charcoal">
      {name?.toUpperCase()}
    </span>
  );
}

/** Loading skeleton for item detail. */
function ItemSkeleton() {
  return (
    <main data-testid="menu-item-skeleton" className="min-h-screen bg-cream">
      <Skeleton height={320} />
      <div className="max-w-2xl mx-auto px-4 py-8">
        <Skeleton height={40} width="70%" />
        <Skeleton height={20} width={80} className="mt-3" />
        <Skeleton height={24} width={60} className="mt-2" />
        <Skeleton count={4} height={16} className="mt-4" />
        <Skeleton height={48} className="mt-8" />
      </div>
    </main>
  );
}

/**
 * MenuItem — Public menu item detail page.
 *
 * Reads `:id` from route params → fetches via `useMenuItem(id)`.
 * Browse-only: shows item detail with no ordering affordance.
 * (SPR-014 — online ordering disabled at launch; "Add to Cart" removed.)
 * Shows 404 if item is not found or inactive.
 */
export default function MenuItem() {
  const { id } = useParams();
  const { item, error, isLoading } = useMenuItem(id);

  if (isLoading) return <ItemSkeleton />;

  // 404 — item not found or inactive
  if (error || !item) return <NotFound404 />;

  return (
    <main data-testid="menu-item-page" className="min-h-screen bg-cream">
      {/* Hero image */}
      {item.imageUrl ? (
        <img
          src={item.imageUrl}
          alt={item.name}
          className="w-full h-64 md:h-80 object-cover"
        />
      ) : (
        <HeroPlaceholder name={item.name} />
      )}

      <div className="max-w-2xl mx-auto px-4 py-8">
        {/* Back link */}
        <Link
          to="/menu"
          className="font-heading tracking-widest text-teal-deep text-sm hover:text-teal inline-flex items-center gap-1 mb-4"
          data-testid="back-to-menu"
        >
          ← BACK TO MENU
        </Link>

        {/* Item name */}
        <h1
          className="font-display text-5xl text-charcoal uppercase leading-tight mt-2"
          data-testid="item-name"
        >
          {item.name}
        </h1>

        {/* Category pill + price row */}
        <div className="flex items-center gap-3 mt-3 flex-wrap">
          {item.categoryName && <CategoryPill name={item.categoryName} />}
          <span
            className="font-display text-3xl text-teal tracking-wide"
            data-testid="item-price"
          >
            {formatCents(item.priceCents)}
          </span>
        </div>

        {/* SPR-014 T-007: temporary, flag-gated "coming soon" notice in place
            of the removed "Add to Cart" affordance. Renders only while
            ONLINE_ORDERING_ENABLED is false (the badge handles the gate). */}
        <div className="mt-5">
          <ComingSoonBadge />
        </div>

        {/* Description */}
        {item.description && (
          <p
            className="font-body text-ink/70 text-base leading-relaxed mt-5"
            data-testid="item-description"
          >
            {item.description}
          </p>
        )}

        {/* SPR-014: online ordering disabled at launch — the "Add to Cart"
            affordance was removed. Direct customers back to the menu. */}
        <div className="mt-8">
          <Link
            to="/menu"
            className="w-full md:w-auto inline-flex items-center justify-center bg-rust text-white font-heading tracking-widest text-lg px-8 py-3 rounded-xl shadow-[0_4px_0_#7f371e] hover:translate-y-0.5 hover:shadow-[0_2px_0_#7f371e] active:translate-y-1 transition-all"
            data-testid="back-to-menu-cta"
          >
            BACK TO MENU
          </Link>
        </div>
      </div>
    </main>
  );
}
